import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tooltip,
} from "antd";
import { DeleteOutlined, NodeIndexOutlined, PlusOutlined, SaveFilled } from "@ant-design/icons";

import { createAsset, getAssetCategories, getAssets } from "../../API/assets";
import { getControlsArea } from "../../API/controls";
import {
  createDependency,
  deleteDependency,
  getDependencyTypes,
  getProcessDependencies,
  patchDependency,
} from "../../API/dependencies";
import { createWorkflow, deleteWorkflow, getWorkflows } from "../../API/workflows";
import Assetia from "../Common/Assetia/Assetia";
import ExportButton from "../Common/ExportButton/ExportButton";
import PageTitle from "../Common/PageTitle/PageTitle";
import WithPermission from "../Common/WithPermission/WithPermission";
import { useAuth } from "../Context/AuthContext";
import ProcessesSearch from "./components/ProcessesSearch/ProcessesSearch";
import Columns from "./components/ProcessesTable/columns";
import "./Processes.css";

const { Option } = Select;

const getErrorDescription = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Ocurrió un error inesperado.";
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  const firstValue = Object.values(data)[0];
  return Array.isArray(firstValue) ? String(firstValue[0]) : String(firstValue);
};

const Processes = () => {
  const navigate = useNavigate();
  const { hasPermission, userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;
  const hasAnyRole = userData?.roles?.length > 0;
  const canDependenciesRead = userData ? hasPermission("dependencies", "read") : false;
  const canDependenciesCreate = userData ? hasPermission("dependencies", "create") : false;
  const canAssetsCreate = userData ? hasPermission("assets", "create") : false;

  const [api, contextHolder] = notification.useNotification();
  const [subprocesses, setSubprocesses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [processAssets, setProcessAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [areas, setAreas] = useState([]);
  const [subprocessType, setSubprocessType] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingWorkflowCode, setOpeningWorkflowCode] = useState(null);
  const [viewingWorkflowCode, setViewingWorkflowCode] = useState(null);
  const [deletingWorkflowCode, setDeletingWorkflowCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubprocess, setEditingSubprocess] = useState(null);
  const [creationMode, setCreationMode] = useState("new");
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  const fetchSubprocesses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProcessDependencies();
      setSubprocesses(data);
      setFilteredData(data);
      setSearchText("");
    } catch (error) {
      api.error({
        message: "Error al cargar los procesos.",
        description: getErrorDescription(error),
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSubprocesses();
  }, [fetchSubprocesses]);

  useEffect(() => {
    if (isModalOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isModalOpen]);

  const loadFormConfiguration = async () => {
    const [assetData, categoryData, areaData, dependencyTypes] = await Promise.all([
      getAssets({ allows_workflow: true }),
      getAssetCategories(),
      getControlsArea("assets"),
      getDependencyTypes(),
    ]);
    const enabledCategories = categoryData.filter((category) => category.allows_workflow);
    const allowedAreas = areaData.filter((area) => isSuperuser || hasPermission("assets", "create", area.id));
    const type = dependencyTypes.find((item) => item.name?.toLowerCase() === "flujo de proceso")
      || dependencyTypes.find((item) => item.name?.toLowerCase().includes("proceso"));

    setProcessAssets(assetData);
    setCategories(enabledCategories);
    setAreas(allowedAreas);
    setSubprocessType(type || null);
    return { enabledCategories, type };
  };

  const showCreateModal = async () => {
    try {
      const { enabledCategories } = await loadFormConfiguration();
      const processCategory = enabledCategories.find((category) => category.name?.toLowerCase() === "proceso")
        || enabledCategories[0];
      setEditingSubprocess(null);
      const initialMode = isSuperuser || canAssetsCreate ? "new" : "existing";
      setCreationMode(initialMode);
      form.setFieldsValue({
        process_name: "",
        process_description: "",
        area_id: undefined,
        category_id: processCategory?.id,
        asset_code: undefined,
        subprocesses: [{ name: "", description: "" }],
      });
      setIsModalOpen(true);
    } catch (error) {
      api.error({ message: "Error al preparar el formulario.", description: getErrorDescription(error) });
    }
  };

  const showEditModal = async (subprocess) => {
    try {
      await loadFormConfiguration();
      setEditingSubprocess(subprocess);
      form.setFieldsValue({
        asset_code: subprocess.asset?.code,
        subprocess_name: subprocess.name,
        subprocess_description: subprocess.description || "",
      });
      setIsModalOpen(true);
    } catch (error) {
      api.error({ message: "Error al preparar el subproceso.", description: getErrorDescription(error) });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSubprocess(null);
    setCreationMode("new");
    form.resetFields();
  };

  const createSubprocesses = async (assetCode, values) => {
    if (!subprocessType) throw new Error('No se encontró el tipo de dependencia "Flujo de Proceso".');
    return Promise.all(values.map((subprocess) => createDependency({
      name: subprocess.name.trim(),
      description: subprocess.description?.trim() || "",
      type_id: subprocessType.id,
      asset_code: assetCode,
      atributes: [],
    })));
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editingSubprocess) {
        await patchDependency(editingSubprocess.code, {
          name: values.subprocess_name.trim(),
          description: values.subprocess_description?.trim() || "",
        });
        api.success({ message: "Subproceso actualizado." });
      } else {
        const newSubprocesses = values.subprocesses || [];
        if (!newSubprocesses.length) {
          form.setFields([{ name: "subprocesses", errors: ["Agregá al menos un subproceso."] }]);
          return;
        }

        let assetCode = values.asset_code;
        if (creationMode === "new") {
          const savedAsset = await createAsset({
            name: values.process_name.trim(),
            description: values.process_description?.trim() || "",
            area_id: values.area_id,
            category_id: values.category_id,
          });
          assetCode = savedAsset.code;
        }
        await createSubprocesses(assetCode, newSubprocesses);
        api.success({
          message: creationMode === "new" ? "Proceso y subprocesos agregados." : "Subprocesos agregados.",
        });
      }

      closeModal();
      await fetchSubprocesses();
    } catch (error) {
      api.error({ message: "Error al guardar.", description: getErrorDescription(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code) => {
    try {
      await deleteDependency(code);
      api.success({ message: "Subproceso eliminado." });
      await fetchSubprocesses();
    } catch (error) {
      api.error({ message: "Error al eliminar el subproceso.", description: getErrorDescription(error) });
    }
  };

  const handleOpenWorkflow = async (subprocess) => {
    setOpeningWorkflowCode(subprocess.code);
    try {
      const workflows = await getWorkflows(subprocess.code);
      if (workflows.length > 1) {
        api.warning({ message: "Este subproceso tiene más de un diagrama. Se abrirá el más reciente." });
      }
      const existing = [...workflows].sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0))[0];
      if (existing) {
        navigate(`/workflows/${existing.code}`);
        return;
      }

      const created = await createWorkflow({
        dependency_code: subprocess.code,
        name: `Diagrama de ${subprocess.name}`.slice(0, 200),
        description: "",
        properties: { viewport: { x: 0, y: 0, zoom: 1 } },
        nodes: [],
        edges: [],
      });
      navigate(`/workflows/${created.code}`);
    } catch (error) {
      api.error({ message: "No se pudo abrir el diagrama.", description: getErrorDescription(error) });
    } finally {
      setOpeningWorkflowCode(null);
    }
  };

  const handleViewWorkflow = async (subprocess) => {
    setViewingWorkflowCode(subprocess.code);
    try {
      const workflows = await getWorkflows(subprocess.code);
      const workflow = [...workflows]
        .sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0))[0];
      if (!workflow) {
        api.info({ message: "El subproceso todavía no tiene un diagrama." });
        await fetchSubprocesses();
        return;
      }
      navigate(`/workflows/${workflow.code}?mode=view`);
    } catch (error) {
      api.error({ message: "No se pudo abrir la vista del diagrama.", description: getErrorDescription(error) });
    } finally {
      setViewingWorkflowCode(null);
    }
  };

  const handleDeleteWorkflow = async (subprocess) => {
    setDeletingWorkflowCode(subprocess.code);
    try {
      const workflows = await getWorkflows(subprocess.code);
      const orderedWorkflows = [...workflows]
        .sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0));
      const workflow = orderedWorkflows[0];
      if (!workflow) {
        api.info({ message: "El subproceso no tiene un diagrama para eliminar." });
        await fetchSubprocesses();
        return;
      }

      await deleteWorkflow(workflow.code);
      api.success({ message: "Diagrama eliminado." });
      if (orderedWorkflows.length > 1) {
        api.warning({
          message: "El subproceso tenía más de un diagrama.",
          description: "Se eliminó el más reciente; los diagramas anteriores permanecen disponibles.",
        });
      }
      await fetchSubprocesses();
    } catch (error) {
      api.error({ message: "No se pudo eliminar el diagrama.", description: getErrorDescription(error) });
    } finally {
      setDeletingWorkflowCode(null);
    }
  };

  const tableFilters = {
    processes: [...new Set(subprocesses.map((item) => item.asset?.name).filter(Boolean))],
    subprocesses: [...new Set(subprocesses.map((item) => item.name).filter(Boolean))],
    areas: [...new Set(subprocesses.map((item) => item.asset?.area?.name).filter(Boolean))],
    types: [...new Set(subprocesses.map((item) => typeof item.type === "string" ? item.type : item.type?.name).filter(Boolean))],
  };

  const currentPermission = isSuperuser || canDependenciesRead;
  const canAdd = isSuperuser || canDependenciesCreate;

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={currentPermission}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start"><Col><PageTitle title="Procesos" icon="processes" /></Col></Row>
            <Row justify="start" gutter={[0, 16]}>
              <Col span={18}>
                <Tooltip title={!canAdd ? "No tiene permisos para agregar subprocesos" : ""}>
                  <span style={{ display: "inline-block" }}>
                    <Button className="add-button" onClick={showCreateModal} disabled={!canAdd}>
                      <PlusOutlined /> Agregar Nuevo
                    </Button>
                  </span>
                </Tooltip>
              </Col>
              <Col span={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                <ProcessesSearch
                  processesData={subprocesses}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>
            <Row justify="end" gutter={[8, 0]}>
              <Col>
                <ExportButton
                  api={api}
                  contextHolder={contextHolder}
                  data={filteredData}
                  filename="Procesos.csv"
                  transformData={(data) => data.map((item) => ({
                    Proceso: item.asset?.name || "N/A",
                    Subproceso: item.name,
                    Descripción: item.description || "",
                    Responsable: item.asset?.area?.name || "N/A",
                    Tipo: (typeof item.type === "string" ? item.type : item.type?.name) || "N/A",
                    Diagrama: item.workflows_count > 0 ? "Diagramado" : "Sin diagrama",
                  }))}
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination processes-table"
              columns={Columns({
                showEditModal,
                handleDelete,
                handleOpenWorkflow,
                handleViewWorkflow,
                handleDeleteWorkflow,
                filters: tableFilters,
                openingWorkflowCode,
                viewingWorkflowCode,
                deletingWorkflowCode,
              })}
              dataSource={filteredData}
              scroll={{ y: 450, x: 1400 }}
              rowKey="code"
              onRow={(record) => ({
                onDoubleClick: (event) => {
                  const interactiveElement = event.target.closest(
                    "button, a, input, textarea, select, [role='button']"
                  );

                  if (!interactiveElement && openingWorkflowCode !== record.code) {
                    handleOpenWorkflow(record);
                  }
                },
              })}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />
          </Spin>

          <Modal
            className="dependency-modal processes-create-modal"
            width={760}
            title={<span><NodeIndexOutlined style={{ marginRight: 8 }} />{editingSubprocess ? "Editar Subproceso" : "Agregar Nuevo"}</span>}
            open={isModalOpen}
            onCancel={closeModal}
            footer={[
              <Button key="cancel" className="cancel-button" onClick={closeModal}>Cancelar</Button>,
              <Button key="save" className="save-type-button" loading={saving} onClick={() => form.submit()} icon={<SaveFilled />}>Guardar</Button>,
            ]}
          >
            <Form form={form} layout="vertical" onFinish={handleSave}>
              {editingSubprocess ? (
                <>
                  <Form.Item label="Proceso">
                    <Input value={editingSubprocess.asset?.name || ""} disabled />
                  </Form.Item>
                  <Form.Item name="subprocess_name" label="Nombre del subproceso" rules={[{ required: true, whitespace: true, message: "Campo requerido" }]}>
                    <Input.TextArea ref={inputRef} maxLength={200} showCount style={{ resize: "none" }} />
                  </Form.Item>
                  <Form.Item name="subprocess_description" label="Descripción">
                    <Input.TextArea maxLength={2000} showCount style={{ resize: "none", height: 100 }} />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Tabs
                    className="processes-create-tabs"
                    activeKey={creationMode}
                    onChange={setCreationMode}
                    items={[
                      {
                        key: "new",
                        label: "Nuevo proceso",
                        disabled: !isSuperuser && !canAssetsCreate,
                      },
                      { key: "existing", label: "Nuevo subproceso" },
                    ]}
                  />

                  {creationMode === "new" ? (
                    <>
                      <Form.Item name="process_name" label="Nombre" rules={[{ required: true, whitespace: true, message: "Campo requerido" }]}> 
                        <Input.TextArea ref={inputRef} maxLength={200} showCount style={{ resize: "none" }} />
                      </Form.Item>
                      <Form.Item name="process_description" label="Descripción" rules={[{ required: true, whitespace: true, message: "Campo requerido" }]}>
                        <Input.TextArea maxLength={2000} showCount style={{ resize: "none", height: 90 }} />
                      </Form.Item>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Form.Item name="area_id" label="Responsable" rules={[{ required: true, message: "Responsable requerido" }]}>
                            <Select showSearch placeholder="Seleccione un Área" optionFilterProp="children">
                              {areas.map((area) => <Option key={area.id} value={area.id}>{area.name}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="category_id" label="Categoría" rules={[{ required: true, message: "Categoría requerida" }]}>
                            <Select disabled placeholder="Seleccione una Categoría">
                              {categories.map((category) => <Option key={category.id} value={category.id}>{category.name}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <Form.Item name="asset_code" label="Proceso" rules={[{ required: true, message: "Seleccioná un proceso" }]}>
                      <Select showSearch placeholder="Seleccione un Proceso" optionFilterProp="children">
                        {processAssets.map((asset) => <Option key={asset.code} value={asset.code}>{asset.name}</Option>)}
                      </Select>
                    </Form.Item>
                  )}

                  <Form.List name="subprocesses">
                    {(fields, { add, remove }, { errors }) => (
                      <div className="processes-subprocess-section">
                        <div className="processes-subprocess-heading">
                          <span className="processes-subprocess-title">
                            <span className="processes-required-mark">*</span>
                            Subproceso
                          </span>
                          <Button
                            type="text"
                            className="add-type-button processes-add-subprocess-button"
                            icon={<PlusOutlined />}
                            onClick={() => add({ name: "", description: "" })}
                            aria-label="Agregar subproceso"
                          />
                        </div>
                        <div className="processes-subprocess-list">
                          {fields.map((field) => (
                            <Space key={field.key} align="start" className="processes-subprocess-row">
                              <Form.Item name={[field.name, "name"]} rules={[{ required: true, whitespace: true, message: "Nombre requerido" }]}>
                                <Input placeholder="Nombre del subproceso" maxLength={200} />
                              </Form.Item>
                              <Form.Item name={[field.name, "description"]}>
                                <Input placeholder="Descripción" maxLength={2000} />
                              </Form.Item>
                              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                            </Space>
                          ))}
                          {!fields.length && <div className="processes-empty-subprocesses">Agregá al menos un subproceso.</div>}
                          <Form.ErrorList errors={errors} />
                        </div>
                      </div>
                    )}
                  </Form.List>
                </>
              )}
            </Form>
          </Modal>
        </div>
        <Row style={{ marginTop: 80 }}>
          <Assetia pageTitle="procesos" pageDescription="cargar tus procesos y gestionarlos de manera simple" view="processes" />
        </Row>
      </>
    </WithPermission>
  );
};

export default Processes;
